import React, { useMemo } from 'react';
import {
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  isToday,
  format,
  parseISO,
  setHours,
  getHours,
  getMinutes
} from 'date-fns';
import EventPill from './EventPill';

/**
 * WeekView - 7-column time grid calendar view
 *
 * Features:
 * - 7 day columns (Sun-Sat)
 * - Hourly rows (24h or business hours)
 * - 30-minute slots
 * - Events positioned by time
 * - Click on time slot to quick-add
 * - Today column highlighted
 */

const HOURS_START = 6; // 6 AM
const HOURS_END = 24; // Midnight (show full evening hours)
const SLOT_HEIGHT = 48; // pixels per hour

const WeekView = ({
  currentDate,
  events = [],
  onDateClick,
  onEventClick,
  loading
}) => {
  // Generate week days
  const weekDays = useMemo(() => {
    const weekStart = startOfWeek(currentDate, { weekStartsOn: 0 });
    const weekEnd = endOfWeek(currentDate, { weekStartsOn: 0 });
    return eachDayOfInterval({ start: weekStart, end: weekEnd });
  }, [currentDate]);

  // Generate hours for the time column
  const hours = useMemo(() => {
    const result = [];
    for (let h = HOURS_START; h < HOURS_END; h++) {
      result.push(h);
    }
    return result;
  }, []);

  // Group events by day and sort by time
  const eventsByDay = useMemo(() => {
    const grouped = {};

    weekDays.forEach(day => {
      const dateKey = format(day, 'yyyy-MM-dd');
      grouped[dateKey] = [];
    });

    events.forEach(event => {
      if (!event.startTime) return;

      try {
        const eventDate = parseISO(event.startTime);
        const dateKey = format(eventDate, 'yyyy-MM-dd');

        if (grouped[dateKey]) {
          grouped[dateKey].push(event);
        }
      } catch (err) {
        console.warn('Invalid event date:', event.startTime);
      }
    });

    // Sort events within each day
    Object.keys(grouped).forEach(dateKey => {
      grouped[dateKey].sort((a, b) => {
        if (a.allDay && !b.allDay) return -1;
        if (!a.allDay && b.allDay) return 1;
        return new Date(a.startTime) - new Date(b.startTime);
      });
    });

    return grouped;
  }, [events, weekDays]);

  /**
   * Calculate overlapping event positions
   *
   * Layout Rules:
   * 1. Same start AND end time → Side-by-side (equal width columns)
   * 2. Different durations or one starts within another → Layered with offset
   * 3. Content: base layer = top aligned, overlapping layers = center aligned
   * 4. If title would be hidden behind layered event → use side-by-side instead
   */
  const getEventLayout = (dayEvents) => {
    const timedEvents = dayEvents.filter(e => !e.allDay);
    if (timedEvents.length === 0) return {};

    // Parse event times
    const eventTimes = timedEvents.map(event => {
      const start = parseISO(event.startTime);
      const end = event.endTime ? parseISO(event.endTime) : start;
      const startMinutes = getHours(start) * 60 + getMinutes(start);
      const endMinutes = getHours(end) * 60 + getMinutes(end);
      const duration = Math.max(endMinutes - startMinutes, 30);
      return {
        id: event.id,
        start: startMinutes,
        end: Math.max(endMinutes, startMinutes + 30),
        duration
      };
    });

    // Group events that have IDENTICAL start AND end times (side-by-side)
    const identicalTimeGroups = {};
    eventTimes.forEach(event => {
      const key = `${event.start}-${event.end}`;
      if (!identicalTimeGroups[key]) {
        identicalTimeGroups[key] = [];
      }
      identicalTimeGroups[key].push(event);
    });

    // Sort by start time, then by duration (longer first for base layer)
    eventTimes.sort((a, b) => {
      if (a.start !== b.start) return a.start - b.start;
      return (b.duration) - (a.duration); // Longer duration first
    });

    const layout = {}; // eventId -> { column, totalColumns, layer, offsetPercent, widthPercent, zIndex, contentAlign }

    eventTimes.forEach((event) => {
      const timeKey = `${event.start}-${event.end}`;
      const identicalGroup = identicalTimeGroups[timeKey];

      // Check if this event has identical-time siblings (same start AND end)
      if (identicalGroup.length > 1) {
        // Side-by-side layout for identical times
        const columnIndex = identicalGroup.findIndex(e => e.id === event.id);
        const totalColumns = identicalGroup.length;
        const widthPercent = 100 / totalColumns;
        const offsetPercent = columnIndex * widthPercent;

        layout[event.id] = {
          mode: 'side-by-side',
          column: columnIndex,
          totalColumns,
          offsetPercent,
          widthPercent,
          zIndex: 1,
          contentAlign: 'top' // All side-by-side events align top
        };
        return;
      }

      // Find all events that overlap with this one (but NOT identical times)
      const overlapping = eventTimes.filter(other =>
        other.id !== event.id &&
        other.start < event.end &&
        other.end > event.start &&
        !(other.start === event.start && other.end === event.end) // Exclude identical times
      );

      if (overlapping.length === 0) {
        // No overlaps - full width
        layout[event.id] = {
          mode: 'single',
          layer: 0,
          offsetPercent: 0,
          widthPercent: 100,
          zIndex: 1,
          contentAlign: 'top'
        };
      } else {
        // Find events that have already been positioned and overlap with this one
        const positionedOverlaps = overlapping.filter(o => layout[o.id] && layout[o.id].mode !== 'side-by-side');

        if (positionedOverlaps.length === 0) {
          // First event in overlap group - takes full width, content at top
          layout[event.id] = {
            mode: 'layered',
            layer: 0,
            offsetPercent: 0,
            widthPercent: 100,
            zIndex: 1,
            contentAlign: 'top' // Base layer: content at top
          };
        } else {
          // Calculate layer based on positioned overlaps
          const maxLayer = Math.max(...positionedOverlaps.map(o => layout[o.id].layer));
          const newLayer = maxLayer + 1;

          // Offset increases with each layer (15% per layer for better visibility)
          const offsetPercent = Math.min(newLayer * 15, 40); // Cap at 40%

          // Width decreases with each layer
          const widthPercent = 100 - offsetPercent;

          layout[event.id] = {
            mode: 'layered',
            layer: newLayer,
            offsetPercent,
            widthPercent,
            zIndex: newLayer + 1, // Higher layer = higher z-index (on top)
            contentAlign: 'center' // Overlapping layers: content centered
          };
        }
      }
    });

    return layout;
  };

  // Calculate event position and height
  const getEventStyle = (event) => {
    if (event.allDay) return null;

    try {
      const start = parseISO(event.startTime);
      const end = event.endTime ? parseISO(event.endTime) : start;

      const startHour = getHours(start) + getMinutes(start) / 60;
      const endHour = getHours(end) + getMinutes(end) / 60;

      const top = (startHour - HOURS_START) * SLOT_HEIGHT;
      const height = Math.max((endHour - startHour) * SLOT_HEIGHT, SLOT_HEIGHT / 2);

      return {
        top: `${top}px`,
        height: `${height}px`,
        minHeight: '24px'
      };
    } catch {
      return null;
    }
  };

  // Handle time slot click
  const handleTimeSlotClick = (day, hour, e) => {
    if (onDateClick) {
      const time = `${hour.toString().padStart(2, '0')}:00`;
      const rect = e.currentTarget.getBoundingClientRect();
      onDateClick(day, {
        x: rect.left + rect.width / 2,
        y: rect.top + rect.height / 2
      }, time);
    }
  };

  // Handle event click
  const handleEventClick = (event) => {
    if (onEventClick) {
      onEventClick(event);
    }
  };

  // Get all-day events for a day
  const getAllDayEvents = (day) => {
    const dateKey = format(day, 'yyyy-MM-dd');
    return (eventsByDay[dateKey] || []).filter(e => e.allDay);
  };

  // Get timed events for a day
  const getTimedEvents = (day) => {
    const dateKey = format(day, 'yyyy-MM-dd');
    return (eventsByDay[dateKey] || []).filter(e => !e.allDay);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand"></div>
      </div>
    );
  }

  return (
    <div className="select-none overflow-x-auto">
      {/* Header row with day names */}
      <div className="flex border-b border-gray-200 bg-gray-50 min-w-[500px]">
        {/* Time column header */}
        <div className="w-12 sm:w-16 flex-shrink-0 border-r border-gray-200"></div>

        {/* Day headers */}
        {weekDays.map(day => (
          <div
            key={day.toISOString()}
            className={`flex-1 min-w-[60px] py-1 sm:py-2 text-center border-r border-gray-200 ${
              isToday(day) ? 'bg-yellow-50' : ''
            }`}
          >
            <div className="text-xs font-medium text-gray-500 uppercase">
              <span className="hidden sm:inline">{format(day, 'EEE')}</span>
              <span className="sm:hidden">{format(day, 'EEEEE')}</span>
            </div>
            <div className={`text-sm sm:text-lg font-semibold ${
              isToday(day) ? 'text-yellow-600' : 'text-gray-900'
            }`}>
              {format(day, 'd')}
            </div>
          </div>
        ))}
      </div>

      {/* All-day events row */}
      <div className="flex border-b border-gray-200 bg-white min-h-[32px] sm:min-h-[40px] min-w-[500px]">
        <div className="w-12 sm:w-16 flex-shrink-0 border-r border-gray-200 px-1 sm:px-2 py-1">
          <span className="text-xs text-gray-500 hidden sm:inline">All day</span>
          <span className="text-xs text-gray-500 sm:hidden">All</span>
        </div>
        {weekDays.map(day => {
          const allDayEvents = getAllDayEvents(day);
          return (
            <div
              key={day.toISOString()}
              className={`flex-1 p-1 border-r border-gray-200 space-y-0.5 ${
                isToday(day) ? 'bg-yellow-50/50' : ''
              }`}
            >
              {allDayEvents.map(event => (
                <EventPill
                  key={event.id}
                  event={event}
                  onClick={handleEventClick}
                  size="sm"
                  showTime={false}
                />
              ))}
            </div>
          );
        })}
      </div>

      {/* Time grid */}
      <div className="flex overflow-auto min-w-[500px]" style={{ maxHeight: 'calc(100vh - 350px)' }}>
        {/* Time labels column */}
        <div
          className="w-12 sm:w-16 flex-shrink-0 border-r border-gray-200 bg-white"
          style={{ minHeight: `${(HOURS_END - HOURS_START) * SLOT_HEIGHT}px` }}
        >
          {hours.map((hour, index) => (
            <div
              key={hour}
              className="relative"
              style={{ height: `${SLOT_HEIGHT}px` }}
            >
              <span className={`absolute left-0 right-0 text-center text-xs text-gray-500 ${
                index === 0 ? 'top-1' : 'top-0 -translate-y-1/2'
              }`}>
                <span className="hidden sm:inline">{format(setHours(new Date(), hour), 'h a')}</span>
                <span className="sm:hidden">{format(setHours(new Date(), hour), 'ha').toLowerCase()}</span>
              </span>
            </div>
          ))}
        </div>

        {/* Day columns with events */}
        {weekDays.map(day => {
          const dateKey = format(day, 'yyyy-MM-dd');
          const dayEvents = eventsByDay[dateKey] || [];
          const timedEvents = getTimedEvents(day);
          const eventLayout = getEventLayout(dayEvents);
          const isCurrentDay = isToday(day);

          return (
            <div
              key={day.toISOString()}
              className={`flex-1 min-w-[60px] relative border-r border-gray-200 ${
                isCurrentDay ? 'bg-yellow-50/30' : 'bg-white'
              }`}
              style={{ minHeight: `${(HOURS_END - HOURS_START) * SLOT_HEIGHT}px` }}
            >
              {/* Hour slots */}
              {hours.map(hour => (
                <div
                  key={hour}
                  onClick={(e) => handleTimeSlotClick(day, hour, e)}
                  className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer transition-colors"
                  style={{ height: `${SLOT_HEIGHT}px` }}
                >
                  {/* Half-hour line */}
                  <div className="absolute left-0 right-0 border-t border-gray-100 border-dashed"
                       style={{ top: `${(hour - HOURS_START) * SLOT_HEIGHT + SLOT_HEIGHT / 2}px` }} />
                </div>
              ))}

              {/* Events overlay */}
              <div className="absolute inset-0 pointer-events-none px-0.5">
                {timedEvents.map(event => {
                  const style = getEventStyle(event);
                  if (!style) return null;

                  // Get layout positioning (side-by-side or layered)
                  const layoutInfo = eventLayout[event.id] || {
                    offsetPercent: 0,
                    widthPercent: 100,
                    zIndex: 1,
                    contentAlign: 'top'
                  };

                  return (
                    <div
                      key={event.id}
                      className="absolute pointer-events-auto px-0.5"
                      style={{
                        ...style,
                        left: `${layoutInfo.offsetPercent}%`,
                        width: `${layoutInfo.widthPercent}%`,
                        zIndex: layoutInfo.zIndex
                      }}
                    >
                      <EventPill
                        event={event}
                        onClick={handleEventClick}
                        size="sm"
                        showTime={true}
                        contentAlign={layoutInfo.contentAlign || 'top'}
                      />
                    </div>
                  );
                })}
              </div>

              {/* Current time indicator */}
              {isCurrentDay && (
                <CurrentTimeIndicator hoursStart={HOURS_START} slotHeight={SLOT_HEIGHT} />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

/**
 * Current time indicator line
 */
const CurrentTimeIndicator = ({ hoursStart, slotHeight }) => {
  const now = new Date();
  const currentHour = getHours(now) + getMinutes(now) / 60;
  const top = (currentHour - hoursStart) * slotHeight;

  // Only show if within visible hours
  if (top < 0) return null;

  return (
    <div
      className="absolute left-0 right-0 flex items-center pointer-events-none z-10"
      style={{ top: `${top}px` }}
    >
      <div className="w-2 h-2 rounded-full bg-red-500 -ml-1"></div>
      <div className="flex-1 h-0.5 bg-red-500"></div>
    </div>
  );
};

export default WeekView;
